import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Button, Col } from "reactstrap";
import TextField from "../../../common/TextField";
import { toast } from "react-toastify";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import { createSuburbService } from "../../../../Services/CRMAdminServices/CRMSuburb";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { getAllAreas, getAllStates } from "../../../../Services/MMPClientServices/PropertySearchService";
import DropdownWithValueName from "../../../common/DropdownWithValueName";
import { H3 } from "../../../../AbstractElements";

// --- Sanitizers -------------------------------------------------------
// Growth% / Vacancy: numbers only, max 2 decimal places. Operates on the
// whole string (not per-keystroke regex test) so paste is sanitized too,
// not just typed input.
function sanitizeDecimalInput(raw: string, maxIntDigits: number, maxDecimals: number) {
    let cleaned = raw.replace(/[^0-9.]/g, "");
    const firstDot = cleaned.indexOf(".");
    if (firstDot !== -1) {
        cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
    }
    const [intPart, decPart] = cleaned.split(".");
    const limitedInt = (intPart || "").slice(0, maxIntDigits);
    if (decPart !== undefined) {
        return `${limitedInt}.${decPart.slice(0, maxDecimals)}`;
    }
    return limitedInt;
}

// KM to CBD / KM to Rail: whole numbers only, no negative sign, no decimal.
function sanitizeWholeNumberInput(raw: string, maxDigits: number) {
    return raw.replace(/[^0-9]/g, "").slice(0, maxDigits);
}

// Removes tabs and collapses/trims whitespace so pasted content with stray
// tabs/spaces never lands in a saved text field.
function cleanText(value: string) {
    return value.replace(/\t/g, " ").trim();
}

function isValidDecimal(value: string) {
    if (value === "") return true; // optional field
    return /^\d{1,3}(\.\d{1,2})?$/.test(value);
}

function isValidWholeNumber(value: string) {
    if (value === "") return true; // optional field
    return /^\d+$/.test(value);
}

// Fixed Yes/No option sets for Employment Hub / Major Road Access — first
// option ("Yes") is used as the default selected value so the dropdown
// never shows a blank "Select"/"All" placeholder and there is always a
// valid value to save even if the user never touches the field.
const yesNoOptions = [
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
];

export default function AddCRMSuburb() {
    const queryClient = useQueryClient();
    const navigate = useSafeNavigate();
    const [formErrors, setFormErrors] = useState<any>({});
    const [statesData, setStatesData] = useState([]);
    const [areasData, setAreasData] = useState([]);
    const [formData, setFormData] = useState({
        suburbName: "",
        postcode: "",
        areaId: "",
        stateId: "",
        growthRating: "",
        vacancy: "",
        populationTrend: "",
        dwellingDemand: "",
        employmentHubs: yesNoOptions[0].value, // default "Yes" — no blank Select/All option shown
        schoolCatchments: "",
        majorRoadAccess: yesNoOptions[0].value, // default "Yes" — no blank Select/All option shown
        nearestCBD: "",
        kmToCBD: "",
        nearestRail: "",
        kmToRail: "",
    });

    const {
        data: allStatesData,
        isLoading: allStatesLoading,
        isError: allStatesIsError,
    } = useGetRequestQuery(
        ["getAllStates"],
        getAllStates,
        {
            enabled: true,
        }
    );

    useEffect(() => {
        if (allStatesData?.data && !allStatesIsError && !allStatesLoading) {
            const transformedData = allStatesData.data.map((state: any) => ({
                value: state.id,
                label: state.name
            }));
            setStatesData(transformedData);
        }
    }, [allStatesData, allStatesIsError, allStatesLoading]);

    const {
        data: allAreasData,
        isLoading: allAreasLoading,
        isError: allAreasIsError,
    } = useGetRequestQuery(
        ["getAllAreas", formData.stateId],
        () => getAllAreas({
            searchId: formData.stateId,
            searchType: "area"
        }),
        {
            enabled: !!formData.stateId,
        }
    );

    useEffect(() => {
        if (allAreasData?.data && !allAreasIsError && !allAreasLoading) {
            const transformedData = allAreasData?.data?.map((area: any) => ({
                value: area.id,
                label: area.name
            })).sort((a: { label: string; value: string }, b: { label: string; value: string }) =>
                a.label.localeCompare(b.label)
            );
            setAreasData(transformedData);
        }
    }, [allAreasData, allAreasIsError, allAreasLoading]);
 const handleValueChange = (name: string, value: string) => {
        if (name === "growthRating" || name === "vacancy") {
            setFormData((prev) => ({ ...prev, [name]: sanitizeDecimalInput(value, 3, 2) }));
            setFormErrors((prev: any) => ({ ...prev, [name]: "" }));
            return;
        }

        if (name === "kmToCBD" || name === "kmToRail") {
            if (value.includes(".")) {
                // Clear the field entirely on an invalid (decimal) entry — this
                // guarantees "7.5" can never become "75", and also stops a
                // rejected decimal from silently letting the next keystroke
                // continue on (e.g. typing "3", then ".", then "2" would
                // otherwise become "32" since "3" was still sitting there).
                setFormData((prev) => ({ ...prev, [name]: "" }));
                setFormErrors((prev: any) => ({
                    ...prev,
                    [name]: "Whole numbers only — decimals are not allowed. The field has been cleared.",
                }));
                return;
            }

            setFormData((prev) => ({ ...prev, [name]: sanitizeWholeNumberInput(value, 4) }));
            setFormErrors((prev: any) => ({ ...prev, [name]: "" }));
            return;
        }

        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
        setFormErrors((prevErrors: any) => ({
            ...prevErrors,
            [name]: "",
        }));
    };
    // Mutation for adding a suburb
    const { mutate: addSuburb, isPending: isAddingSuburb } = useMutationRequestQuery(
        "createSuburbService",
        createSuburbService,
        {
            onSuccess: () => {
                toast.success("Suburb added successfully.");
                navigate("/CRMSuburb");
                queryClient.invalidateQueries({ queryKey: ["getAllActiveSuburbsService"] });
            },
            onError: (err: any) => {
                const status = err?.response?.status;
                if (status === 409) {
                    // Expected — backend blocks a duplicate suburb (same name + area/state).
                    toast.error(
                        err?.response?.data?.message ||
                            "This suburb already exists for the selected area/state."
                    );
                } else {
                    toast.error(err?.response?.data?.message || "Failed to add suburb.");
                }
            },
        }
    );

    // Handle form submission
    const handleSubmit = (e: any) => {
        e.preventDefault();
        let errors: any = {};

        // Trim/clean text fields before validating so leading/trailing
        // whitespace and stray tabs never count as "filled in" or get saved.
        const cleaned = {
            ...formData,
            suburbName: cleanText(formData.suburbName),
            postcode: cleanText(formData.postcode),
            nearestCBD: cleanText(formData.nearestCBD),
            nearestRail: cleanText(formData.nearestRail),
        };

        if (!cleaned.suburbName) errors.suburbName = "Suburb Name is required!";
        if (!cleaned.postcode) errors.postcode = "Postcode is required!";
        if (!cleaned.areaId) errors.areaId = "Area ID is required!";
        if (!cleaned.stateId) errors.stateId = "State ID is required!";

        // Employment Hub and Major Road Access are mandatory Yes/No fields.
        if (!cleaned.employmentHubs) errors.employmentHubs = "Employment Hub is required!";
        if (!cleaned.majorRoadAccess) errors.majorRoadAccess = "Major Road Access is required!";

        if (!isValidDecimal(cleaned.growthRating)) {
            errors.growthRating = "Enter a number with up to 2 decimal places.";
        }
        if (!isValidDecimal(cleaned.vacancy)) {
            errors.vacancy = "Enter a number with up to 2 decimal places.";
        }
       if (!isValidWholeNumber(cleaned.kmToCBD)) {
    errors.kmToCBD = "Whole numbers only — decimals are not allowed.";
}
if (!isValidWholeNumber(cleaned.kmToRail)) {
    errors.kmToRail = "Whole numbers only — decimals are not allowed.";
}
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        // Derive stateName and areaName from selected IDs so API receives both id and name
        const selectedState = (statesData as any[]).find(
            (s: any) => String(s.value) === String(cleaned.stateId)
        );
        const selectedArea = (areasData as any[]).find(
            (a: any) => String(a.value) === String(cleaned.areaId)
        );

        const payload = {
            ...cleaned,
            stateName: selectedState?.label ?? "",
            areaName: selectedArea?.label ?? "",
        };

        addSuburb(payload);
    };

    return (
        <div className="px-4 py-4" style={{ minHeight: "300px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
            <Form style={{ boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", backgroundColor: "white", borderRadius: "8px", padding: "15px", paddingLeft: "30px" }}>
                <div className="row">
                    <div className="d-flex justify-content-between align-items-center my-3">
                        <H3 className="heading-color f-w-900">Add Suburb</H3>
                    </div>
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="Suburb Name:"
                                type="text"
                                name="suburbName"
                                value={formData.suburbName}
                                setValue={handleValueChange}
                                placeholder="Enter suburb name"
                                passCol="col-10"
                                design="flex"
                                maxChars={50}
                                tabIndex={1}
                                error={formErrors.suburbName}
                                errorMessage={formErrors.suburbName || "Suburb Name is required!"}
                            />
                        </Col>
                    </div>
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="Postcode:"
                                type="text"
                                name="postcode"
                                value={formData.postcode}
                                setValue={handleValueChange}
                                placeholder="Enter postcode"
                                passCol="col-10"
                                design="flex"
                                maxChars={10}
                                tabIndex={2}
                                error={formErrors.postcode}
                                errorMessage={formErrors.postcode || "Postcode is required!"}
                            />
                        </Col>
                    </div>
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <DropdownWithValueName
                                label="State:"
                                name="stateId"
                                value={formData.stateId}
                                setValue={handleValueChange}
                                options={statesData || []}
                                tabIndex={3}
                                passCol="col-10"
                                design="flex"
                                error={formErrors.stateId}
                                errorMessage="State ID is required!"
                            />
                        </Col>
                    </div>

                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <DropdownWithValueName
                                label="Area:"
                                name="areaId"
                                value={formData.areaId}
                                setValue={handleValueChange}
                                options={areasData || []}
                                tabIndex={4}
                                passCol="col-10"
                                design="flex"
                                error={formErrors.areaId}
                                errorMessage="Area ID is required!"
                            />
                        </Col>
                    </div>

                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="Growth Rating:"
                                type="text"
                                name="growthRating"
                                value={formData.growthRating}
                                setValue={handleValueChange}
                                placeholder="Enter growth rating"
                                passCol="col-10"
                                design="flex"
                                maxChars={6}
                                tabIndex={5}
                                error={formErrors.growthRating}
                                errorMessage={formErrors.growthRating}
                            />
                        </Col>
                    </div>

                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="Vacancy:"
                                type="text"
                                name="vacancy"
                                value={formData.vacancy}
                                setValue={handleValueChange}
                                placeholder="Enter vacancy"
                                passCol="col-10"
                                design="flex"
                                maxChars={6}
                                tabIndex={6}
                                error={formErrors.vacancy}
                                errorMessage={formErrors.vacancy}
                            />
                        </Col>
                    </div>

                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="Nearest CBD:"
                                type="text"
                                name="nearestCBD"
                                value={formData.nearestCBD}
                                setValue={handleValueChange}
                                placeholder="Enter nearest CBD"
                                passCol="col-10"
                                design="flex"
                                tabIndex={7}
                            />
                        </Col>
                    </div>

                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="KM to CBD:"
                                type="text"
                                name="kmToCBD"
                                value={formData.kmToCBD}
                                setValue={handleValueChange}
                                placeholder="Enter distance to CBD"
                                passCol="col-10"
                                design="flex"
                                maxChars={4}
                                tabIndex={8}
                                error={formErrors.kmToCBD}
                                errorMessage={formErrors.kmToCBD}
                            />
                        </Col>
                    </div>

                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="Nearest Rail:"
                                type="text"
                                name="nearestRail"
                                value={formData.nearestRail}
                                setValue={handleValueChange}
                                placeholder="Enter nearest rail station"
                                passCol="col-10"
                                design="flex"
                                tabIndex={9}
                            />
                        </Col>
                    </div>

                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="KM to Rail:"
                                type="text"
                                name="kmToRail"
                                value={formData.kmToRail}
                                setValue={handleValueChange}
                                placeholder="Enter distance to rail"
                                passCol="col-10"
                                design="flex"
                                maxChars={4}
                                tabIndex={10}
                                error={formErrors.kmToRail}
                                errorMessage={formErrors.kmToRail}
                            />
                        </Col>
                    </div>

                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <DropdownWithValueName
                                label="Population Trend:"
                                name="populationTrend"
                                value={formData.populationTrend}
                                setValue={handleValueChange}
                                options={[
                                    { label: "Growing", value: "Growing" },
                                    { label: "Stable", value: "Stable" },
                                    { label: "Declining", value: "Declining" },
                                ]}
                                tabIndex={11}
                                passCol="col-10"
                                design="flex"
                            />
                        </Col>
                    </div>

                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <DropdownWithValueName
                                label="Dwelling Demand:"
                                name="dwellingDemand"
                                value={formData.dwellingDemand}
                                setValue={handleValueChange}
                                options={[
                                    { label: "High", value: "High" },
                                    { label: "Medium", value: "Medium" },
                                    { label: "Low", value: "Low" },
                                ]}
                                tabIndex={12}
                                passCol="col-10"
                                design="flex"
                            />
                        </Col>
                    </div>

                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <DropdownWithValueName
                                label="Employment Hub:"
                                name="employmentHubs"
                                value={formData.employmentHubs}
                                setValue={handleValueChange}
                                options={yesNoOptions}
                                hideDefaultOption={true}
                                tabIndex={13}
                                passCol="col-10"
                                design="flex"
                                error={formErrors.employmentHubs}
                                errorMessage="Employment Hub is required!"
                            />
                        </Col>
                    </div>

                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <DropdownWithValueName
                                label="School Catchments:"
                                name="schoolCatchments"
                                value={formData.schoolCatchments}
                                setValue={handleValueChange}
                                options={[
                                    { label: "Yes", value: "Yes" },
                                    { label: "No", value: "No" },
                                ]}
                                tabIndex={14}
                                passCol="col-10"
                                design="flex"
                            />
                        </Col>
                    </div>

                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <DropdownWithValueName
                                label="Major Road Access:"
                                name="majorRoadAccess"
                                value={formData.majorRoadAccess}
                                setValue={handleValueChange}
                                options={yesNoOptions}
                                hideDefaultOption={true}
                                tabIndex={15}
                                passCol="col-10"
                                design="flex"
                                error={formErrors.majorRoadAccess}
                                errorMessage="Major Road Access is required!"
                            />
                        </Col>
                    </div>
                    <div className="d-flex gap-3 mt-3">
                        <Button onClick={() => navigate("/CRMSuburb")} color="secondary">Back to Suburbs</Button>
                        <Button onClick={handleSubmit} color="primary" disabled={isAddingSuburb} type="submit">
                            {isAddingSuburb ? "Saving..." : "Save Suburb"}
                        </Button>
                    </div>
                </div>
            </Form>
        </div>
    );
}